const defaultTheme = require('tailwindcss/defaultTheme');
const colors = require('tailwindcss/colors');
const plugin = require('tailwindcss/plugin');

const remify = (pxValue) => {
  return (parseFloat(pxValue, 10) / 16) + 'rem';
}
const remToPx = (remValue) => {
  return (parseFloat(remValue, 10) * 16) + 'px';
}
const twColourOpacity = ({ opacityVariable, opacityValue, cssVarName }) => {
  if (opacityValue !== undefined) {
    return `rgba(var(--${cssVarName}), ${opacityValue})`
  }
  if (opacityVariable !== undefined) {
    return `rgba(var(--${cssVarName}), var(${opacityVariable}, 1))`
  }
  return `rgb(var(--${cssVarName}))`
};

const colours = {
  'background': 'single',
  'heading': 'single',
  'accent': 'single',
  'paragraph': 'palette',
  'link': 'palette',
  'button': 'palette',
};

const paletteValues = [50,100,200,300,400,500,600,700,800,900,];

const constructPalette = () => {
  const palette = {};
  for (const colour in colours) {
    if (Object.hasOwnProperty.call(colours, colour)) {
      const colourType = colours[colour];

      if(colourType === 'single' ) {
        palette[colour] = ({ opacityVariable, opacityValue }) => {
          return twColourOpacity({ opacityVariable, opacityValue, cssVarName: 'palette__' + colour});
        }
      }
      else if(colourType === 'palette') {
        paletteValues.forEach(suffix => {
          const newKey = colour + '-' + suffix;
          palette[newKey] = ({ opacityVariable, opacityValue }) => { 
            return twColourOpacity({ opacityVariable, opacityValue, cssVarName: 'palette__' + newKey});
          }
        });
      }
    }
  }
  return palette;
}

module.exports = {
  content: ["./_dev/**/*.html","./_dev/**/*.hbs", "./_dev/**/*.js"],
  css: ["./_dev/**/*.scss"],
  theme: {
    screens: {
      'xs': '480px',
      ...defaultTheme.screens,
    },
    extend: {      
      fontFamily: {
        sans: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      },
      borderRadius: {
        'detail': 'var(--theme__border-radius__detail, 0.375rem)',
        'button': 'var(--theme__border-radius__button, 0.375rem)',
        'image': 'var(--theme__border-radius__image, 0.5rem)',
        'pill': 'var(--theme__border-radius__pill, 9999px)',
        'input-sm': 'var(--theme__border-radius__input, 0.25rem)',
        'input-md': 'var(--theme__border-radius__input, 0.375rem)',
        'modal': 'var(--theme__border-radius__modal, 1rem)',
        'card-sm': 'var(--theme__border-radius__card-md, 0.125rem)',
        'card-md': 'var(--theme__border-radius__card-md, 0.375rem)',
        'card-lg': 'var(--theme__border-radius__card-lg, 0.5rem)',
        'card-2xl': 'var(--theme__border-radius__card-2xl, 1rem)',
      },
      opacity: {
        'site-header': 'var(--site-header__opacity, 1)',
      },
      colors: {
        grey: colors.gray,
        palette: constructPalette(),
      },
      spacing: {
        'container-gutter-margin': 'var(--container-gutter-margin, 0rem)',
        'container-gutter-width': 'var(--container-gutter-width, 0rem)',
        'site-header-height': 'var(--site-header__height, 0rem)',
      },
      gridColumn: {
        'full': '1 / -1',
      },
      containers: {
        '8xl': remify('1536px'),
        '9xl': remify('1920px'),
        '10xl': remify('2560px'),
      },
      screens: {
        'until-sm': { 'max': '639px' },
        'until-md': { 'max': '767px' },
        'until-lg': { 'max': '1023px' },
        'until-xl': { 'max': '1399px' },
      },
      minHeight: (theme) => ({
        ...theme('spacing'),
      }),
      minWidth: (theme) => ({
        ...theme('spacing'),
      }),
      typography: ({ theme }) => ({
        palette: {
          css: {
            '--tw-prose-body': twColourOpacity({cssVarName: 'palette__paragraph-800'}),
            '--tw-prose-headings': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-lead': twColourOpacity({cssVarName: 'palette__paragraph-700'}),
            '--tw-prose-links': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-bold': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-counters': twColourOpacity({cssVarName: 'palette__paragraph-600'}),
            '--tw-prose-bullets': twColourOpacity({cssVarName: 'palette__paragraph-400'}),
            '--tw-prose-hr': twColourOpacity({cssVarName: 'palette__paragraph-300'}),
            '--tw-prose-quotes': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-quote-borders': twColourOpacity({cssVarName: 'palette__paragraph-300'}),
            '--tw-prose-captions': twColourOpacity({cssVarName: 'palette__paragraph-700'}),
            '--tw-prose-code': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-pre-code': twColourOpacity({cssVarName: 'palette__paragraph-100'}),
            '--tw-prose-pre-bg': twColourOpacity({cssVarName: 'palette__paragraph-900'}),
            '--tw-prose-th-borders': twColourOpacity({cssVarName: 'palette__paragraph-300'}),
            '--tw-prose-td-borders': twColourOpacity({cssVarName: 'palette__paragraph-200'}),
            
            '--tw-prose-invert-body': twColourOpacity({cssVarName: 'palette__paragraph-200'}),
            '--tw-prose-invert-headings': theme('colors.white'),
            '--tw-prose-invert-lead': twColourOpacity({cssVarName: 'palette__paragraph-300'}),
            '--tw-prose-invert-links': theme('colors.white'),
            '--tw-prose-invert-bold': theme('colors.white'),
            '--tw-prose-invert-counters': twColourOpacity({cssVarName: 'palette__paragraph-400'}),
            '--tw-prose-invert-bullets': twColourOpacity({cssVarName: 'palette__paragraph-600'}),
            '--tw-prose-invert-hr': twColourOpacity({cssVarName: 'palette__paragraph-700'}),
            '--tw-prose-invert-quotes': twColourOpacity({cssVarName: 'palette__paragraph-100'}),
            '--tw-prose-invert-quote-borders': twColourOpacity({cssVarName: 'palette__paragraph-700'}),
            '--tw-prose-invert-captions': twColourOpacity({cssVarName: 'palette__paragraph-400'}),
            '--tw-prose-invert-code': theme('colors.white'),
            '--tw-prose-invert-pre-code': twColourOpacity({cssVarName: 'palette__paragraph-300'}),
            '--tw-prose-invert-pre-bg': 'rgb(0 0 0 / 50%)',
            '--tw-prose-invert-th-borders': twColourOpacity({cssVarName: 'palette__paragraph-600'}),
            '--tw-prose-invert-td-borders': twColourOpacity({cssVarName: 'palette__paragraph-700'}),
          },
        },
      }),
    },
  },
  plugins: [
    require('@tailwindcss/typography'),
    require('@tailwindcss/container-queries'),
    plugin(function({ addVariant }) {
      addVariant('error', '&.error');
      addVariant('expanded', '&[aria-expanded="true"]' );
      addVariant('predecessor-not-expanded', '[aria-expanded="false"] + &' );
    }),
    require('@tailwindcss/typography')({
      className: 'mce-content-body',
    }),
  ],
  corePlugins: {
    // container: false,
  },
}